import { Loading, notify, useConfirmModal } from '@affine/component';
import {
  pushGlobalLoadingEventAtom,
  resolveGlobalLoadingEventAtom,
} from '@affine/component/global-loading';
import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor/blocksuite-editor';
import { reportSnapshotExportFailure } from '@affine/core/modules/doc/services/snapshot-export-diagnostics';
import {
  exportDocsWithSnapshotExportScope,
  resolveSnapshotExportScope,
  type SnapshotExportScopeResolution,
} from '@affine/core/modules/doc/services/snapshot-export-scope-resolver';
import { EditorService } from '@affine/core/modules/editor';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getStreamLainWorkspaceSchema } from '@affine/core/modules/workspace/global-schema';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { ExportManager } from '@blocksuite/affine/blocks/surface';
import {
  docLinkBaseURLMiddleware,
  embedSyncedDocMiddleware,
  HtmlAdapterFactoryIdentifier,
  MarkdownAdapterFactoryIdentifier,
  titleMiddleware,
} from '@blocksuite/affine/shared/adapters';
import { printToPdf } from '@blocksuite/affine/shared/utils';
import type { BlockStdScope } from '@blocksuite/affine/std';
import { type Store, Transformer } from '@blocksuite/affine/store';
import {
  createAssetsArchive,
  discoverDocDependencies,
  download,
  HtmlTransformer,
  MarkdownTransformer,
  PdfTransformer,
} from '@blocksuite/affine/widgets/linked-doc';
import { useLiveData, useService } from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import { nanoid } from 'nanoid';
import { createElement, useCallback, useEffect, useRef, useState } from 'react';

import { useAsyncCallback } from '../affine-async-hooks';
import { useAttachmentSizeDialog } from './use-attachment-size-dialog';

type ExportType =
  | 'pdf'
  | 'html'
  | 'png'
  | 'markdown'
  | 'snapshot'
  | 'pdf-export';

interface ExportHandlerOptions {
  page: Store;
  editorContainer: AffineEditorContainer | null;
  type: ExportType;
  shouldContinue?: () => boolean;
}

interface AdapterResult {
  file: string;
  assetsIds: string[];
}

type AdapterFactoryIdentifier =
  | typeof HtmlAdapterFactoryIdentifier
  | typeof MarkdownAdapterFactoryIdentifier;

interface AdapterConfig {
  identifier: AdapterFactoryIdentifier;
  fileExtension: string; // file extension need to be lower case with dot prefix, e.g. '.md', '.txt', '.html'
  contentType: string;
  indexFileName: string;
}

class ExportCancelledError extends Error {
  constructor() {
    super('Export cancelled.');
    this.name = 'ExportCancelledError';
  }
}

function PngExportProgress({
  description,
  elapsedLabel,
  cancelNote,
}: {
  description: string;
  elapsedLabel: (seconds: number) => string;
  cancelNote: string;
}) {
  const startedAtRef = useRef(Date.now());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  return createElement(
    'div',
    { style: { display: 'grid', gap: 12 } },
    createElement(
      'div',
      { style: { display: 'flex', alignItems: 'center', gap: 12 } },
      createElement(Loading),
      createElement('div', null, description)
    ),
    createElement(
      'div',
      { style: { color: 'var(--affine-text-secondary-color)' } },
      elapsedLabel(elapsedSeconds)
    ),
    createElement(
      'div',
      { style: { color: 'var(--affine-text-secondary-color)' } },
      cancelNote
    )
  );
}

async function exportDoc(
  doc: Store,
  std: BlockStdScope,
  config: AdapterConfig
) {
  const transformer = new Transformer({
    schema: getStreamLainWorkspaceSchema(),
    blobCRUD: doc.workspace.blobSync,
    docCRUD: {
      create: (id: string) => doc.workspace.createDoc(id).getStore({ id }),
      get: (id: string) => doc.workspace.getDoc(id)?.getStore({ id }) ?? null,
      delete: (id: string) => doc.workspace.removeDoc(id),
    },
    middlewares: [
      docLinkBaseURLMiddleware(doc.workspace.id),
      titleMiddleware(doc.workspace.meta.docMetas),
      embedSyncedDocMiddleware('content'),
    ],
  });

  const adapterFactory = std.store.provider.get(config.identifier);
  const adapter = adapterFactory.get(transformer);
  const result = (await adapter.fromDoc(doc)) as AdapterResult;
  const assetIds = result?.assetsIds ?? [];

  if (!result || (!result.file && assetIds.length === 0)) {
    throw new Error('Export produced no downloadable content.');
  }

  const docTitle = doc.meta?.title || 'Untitled';
  const contentBlob = new Blob([result.file], { type: config.contentType });

  let downloadBlob: Blob;
  let name: string;

  if (assetIds.length > 0) {
    if (!transformer.assets) {
      throw new Error('No assets found');
    }
    const zip = await createAssetsArchive(transformer.assets, assetIds);
    await zip.file(config.indexFileName, contentBlob);
    downloadBlob = await zip.generate();
    name = `${docTitle}.zip`;
  } else {
    downloadBlob = contentBlob;
    name = `${docTitle}${config.fileExtension}`;
  }

  download(downloadBlob, name);
}

async function exportToHtml(doc: Store, std?: BlockStdScope) {
  if (!std) {
    // If std is not provided, we use the default export method
    await HtmlTransformer.exportDoc(doc);
  } else {
    await exportDoc(doc, std, {
      identifier: HtmlAdapterFactoryIdentifier,
      fileExtension: '.html',
      contentType: 'text/html',
      indexFileName: 'index.html',
    });
  }
}

async function exportToMarkdown(doc: Store, std?: BlockStdScope) {
  if (!std) {
    // If std is not provided, we use the default export method
    await MarkdownTransformer.exportDoc(doc);
  } else {
    await exportDoc(doc, std, {
      identifier: MarkdownAdapterFactoryIdentifier,
      fileExtension: '.md',
      contentType: 'text/plain',
      indexFileName: 'index.md',
    });
  }
}

async function exportHandler({
  page,
  type,
  editorContainer,
  shouldContinue,
}: ExportHandlerOptions) {
  const std = editorContainer?.std;
  track.$.sharePanel.$.export({
    type,
  });
  switch (type) {
    case 'html':
      await exportToHtml(page, std);
      return;
    case 'markdown':
      await exportToMarkdown(page, std);
      return;
    case 'snapshot':
      throw new Error('Snapshot export is handled by the scope resolver.');
    case 'pdf':
      if (!editorContainer) {
        throw new Error('Print export requires the document to be open.');
      }
      await printToPdf(getPrintableRoot(editorContainer));
      return;
    case 'png': {
      if (!std) {
        throw new Error('PNG export requires the document to be open.');
      }
      const exported = await std.get(ExportManager).exportPng({
        shouldContinue,
      });
      if (!exported) {
        if (shouldContinue?.() === false) {
          throw new ExportCancelledError();
        }
        throw new Error('PNG export produced no downloadable content.');
      }
      return;
    }
    case 'pdf-export': {
      await PdfTransformer.exportDoc(page);
      return;
    }
  }
}

function getPrintableRoot(editorContainer: AffineEditorContainer): HTMLElement {
  const root = editorContainer.origin ?? editorContainer;
  const printableRoot = root.querySelector?.('.affine-page-viewport');
  return printableRoot instanceof HTMLElement ? printableRoot : root;
}

function getRenderedEditorContainerForPage(
  page: Store
): AffineEditorContainer | null {
  const currentEditor = (
    globalThis as { currentEditor?: AffineEditorContainer }
  ).currentEditor;
  if (
    currentEditor?.page?.id === page.id ||
    currentEditor?.doc?.id === page.id
  ) {
    return currentEditor;
  }
  return null;
}

// ─── sl-ht07.4: Multi-Doc Snapshot Export with Dependency Discovery ──────────

/**
 * Resolves dependency doc IDs to Store instances.
 * Returns only docs that exist and can be loaded.
 */
function resolveDependencyStores(page: Store, depDocIds: Set<string>): Store[] {
  const stores: Store[] = [];
  for (const id of depDocIds) {
    const doc = page.workspace.getDoc(id);
    if (doc) {
      stores.push(doc.getStore({ id }));
    }
  }
  return stores;
}

/**
 * Builds a human-readable list of dependency doc titles for the confirm dialog.
 */
function buildDepDescription(depStores: Store[]): string {
  const titles = depStores.map(s => s.meta?.title || 'Untitled');
  return titles.map(t => `• ${t}`).join('\n');
}

export const useExportPage = () => {
  const editor = useService(EditorService).editor;
  const workspaceService = useService(WorkspaceService);
  const editorContainer = useLiveData(editor.editorContainer$);
  const blocksuiteDoc = editor.doc.blockSuiteDoc;
  const pushGlobalLoadingEvent = useSetAtom(pushGlobalLoadingEventAtom);
  const resolveGlobalLoadingEvent = useSetAtom(resolveGlobalLoadingEventAtom);
  const t = useI18n();
  const { closeConfirmModal, openConfirmModal } = useConfirmModal();
  const showAttachmentDialog = useAttachmentSizeDialog();

  // Perform the actual snapshot export with the given docs
  const doSnapshotExport = useCallback(
    async (
      page: Store,
      docs: Store[],
      excludeBlobIds?: Set<string>,
      resolvedScope?: SnapshotExportScopeResolution
    ) => {
      const globalLoadingID = nanoid();
      pushGlobalLoadingEvent({ key: globalLoadingID });
      try {
        await exportDocsWithSnapshotExportScope({
          collection: page.workspace,
          docs,
          workspaceService,
          mode: docs.length === 1 ? 'single-doc' : 'multi-doc',
          excludeBlobIds,
          resolvedScope,
        });
        notify.success({
          title: t['com.streamlain.export.success.title'](),
          message: t['com.streamlain.export.success.message'](),
        });
      } catch (err) {
        reportSnapshotExportFailure('archive-export', err);
        notify.error({
          title: t['com.streamlain.export.error.title'](),
          message: t['com.streamlain.export.error.message'](),
        });
      } finally {
        resolveGlobalLoadingEvent(globalLoadingID);
      }
    },
    [pushGlobalLoadingEvent, resolveGlobalLoadingEvent, t, workspaceService]
  );

  // sl-ht07.5: Shows attachment dialog, then triggers export
  const exportWithAttachmentDialog = useCallback(
    (page: Store, docs: Store[]) => {
      void resolveSnapshotExportScope({
        docs,
        workspaceService,
        mode: docs.length === 1 ? 'single-doc' : 'multi-doc',
      })
        .then(resolvedScope => {
          showAttachmentDialog(resolvedScope.stores, ({ excludeBlobIds }) => {
            void doSnapshotExport(page, docs, excludeBlobIds, resolvedScope);
          });
        })
        .catch(err => {
          reportSnapshotExportFailure('scope-resolution', err);
          notify.error({
            title: t['com.streamlain.export.error.title'](),
            message: t['com.streamlain.export.error.message'](),
          });
        });
    },
    [workspaceService, showAttachmentDialog, doSnapshotExport, t]
  );

  const onClickHandler = useAsyncCallback(
    async (type: ExportType) => {
      const renderedEditorContainer =
        editorContainer ?? getRenderedEditorContainerForPage(blocksuiteDoc);

      // sl-ht07.4: Snapshot export with dependency discovery
      if (type === 'snapshot') {
        track.$.sharePanel.$.export({ type });

        const depDocIds = discoverDocDependencies(blocksuiteDoc);
        const depStores = resolveDependencyStores(blocksuiteDoc, depDocIds);

        if (depStores.length === 0) {
          // No dependencies — export single doc with attachment dialog
          exportWithAttachmentDialog(blocksuiteDoc, [blocksuiteDoc]);
          return;
        }

        // Dependencies found — ask user whether to include them
        const depList = buildDepDescription(depStores);
        openConfirmModal({
          title: 'Include Referenced Documents?',
          description: `This document references ${depStores.length} other document${depStores.length > 1 ? 's' : ''}:\n\n${depList}\n\nIncluding them ensures cross-document links work after import.`,
          confirmText: `Export All (${depStores.length + 1} docs)`,
          cancelText: 'Export Current Only',
          onConfirm: () => {
            exportWithAttachmentDialog(blocksuiteDoc, [
              blocksuiteDoc,
              ...depStores,
            ]);
          },
          onCancel: () => {
            exportWithAttachmentDialog(blocksuiteDoc, [blocksuiteDoc]);
          },
        });
        return;
      }

      const pngExportState = { cancelled: false };
      const shouldContinue =
        type === 'png' ? () => !pngExportState.cancelled : undefined;

      const globalLoadingID = nanoid();
      pushGlobalLoadingEvent({
        key: globalLoadingID,
      });
      try {
        if (type === 'png') {
          openConfirmModal({
            title: t['com.streamlain.export.png.dialog.title'](),
            cancelText: t['com.streamlain.export.png.dialog.close'](),
            confirmButtonOptions: { style: { display: 'none' } },
            onCancel: () => {
              pngExportState.cancelled = true;
            },
            children: createElement(PngExportProgress, {
              description: t['com.streamlain.export.png.dialog.description'](),
              elapsedLabel: (seconds: number) =>
                t['com.streamlain.export.png.dialog.elapsed']({
                  seconds: String(seconds),
                }),
              cancelNote: t['com.streamlain.export.png.dialog.cancel-note'](),
            }),
          });
        }
        await renderedEditorContainer?.updateComplete;
        await exportHandler({
          page: blocksuiteDoc,
          type,
          editorContainer: renderedEditorContainer,
          shouldContinue,
        });
        if (pngExportState.cancelled) {
          return;
        }
        if (type !== 'pdf') {
          notify.success({
            title: t['com.streamlain.export.success.title'](),
            message: t['com.streamlain.export.success.message'](),
          });
        }
      } catch (err) {
        if (err instanceof ExportCancelledError) {
          return;
        }
        console.error(err);
        notify.error({
          title: t['com.streamlain.export.error.title'](),
          message: t['com.streamlain.export.error.message'](),
        });
      } finally {
        if (type === 'png') {
          closeConfirmModal();
        }
        resolveGlobalLoadingEvent(globalLoadingID);
      }
    },
    [
      blocksuiteDoc,
      editorContainer,
      pushGlobalLoadingEvent,
      resolveGlobalLoadingEvent,
      t,
      closeConfirmModal,
      openConfirmModal,
      exportWithAttachmentDialog,
    ]
  );

  return onClickHandler;
};
